import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FunnelInsightsApiClient,
  type PricingDismissalEntry,
} from '../api/insightsClient';

/**
 * Phase 4 W3 (AC-095..097): the durable dismissal store — one map per project
 * on the report-settings doc, shared by hint cards and candidate cards
 * (approved round-4 decision 3). PATCH is owner/admin-gated server-side; a
 * 403/failed load resolves to 'unavailable' and the UI keeps dismissals
 * session-local for the render (never a crash, never silent data loss).
 */

export interface PricingDismissalsApi {
  /** slug → stored entry; {} while loading/unavailable. */
  dismissals: Record<string, PricingDismissalEntry>;
  state: 'loading' | 'ready' | 'unavailable';
  dismiss: (slug: string, entry: { reason: string; note?: string; target: 'hint' | 'candidate' }) => Promise<void>;
  restore: (slug: string) => Promise<void>;
}

export function usePricingDismissals(projectId: string): PricingDismissalsApi {
  const client = useMemo(() => new FunnelInsightsApiClient(), []);
  const [dismissals, setDismissals] = useState<Record<string, PricingDismissalEntry>>({});
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const data = await client.getReportSettings({ projectId });
        if (cancelled) return;
        setDismissals(data.pricingDismissals ?? {});
        setState('ready');
      } catch {
        if (!cancelled) {
          setDismissals({});
          setState('unavailable');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [client, projectId]);

  const dismiss = useCallback(async (
    slug: string,
    entry: { reason: string; note?: string; target: 'hint' | 'candidate' },
  ) => {
    const data = await client.patchReportSettings({
      projectId,
      patch: { pricingDismissals: { [slug]: entry } },
    });
    setDismissals(data.pricingDismissals ?? {});
  }, [client, projectId]);

  const restore = useCallback(async (slug: string) => {
    const data = await client.patchReportSettings({
      projectId,
      patch: { pricingDismissals: { [slug]: null } },
    });
    setDismissals(data.pricingDismissals ?? {});
  }, [client, projectId]);

  return { dismissals, state, dismiss, restore };
}
